import clsx from 'clsx';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

export type DateTimeRangeValue = {
  fromDate: string;
  fromTime: string;
  toDate: string;
  toTime: string;
};

export function toDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function todayDateInputValue(): string {
  return toDateInputValue(new Date());
}

export function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

export function localDateTimeMs(date: string, time: string, isEnd: boolean): number {
  if (!time) {
    const value = new Date(`${date}T00:00:00`);
    if (isEnd) value.setDate(value.getDate() + 1);
    return value.getTime();
  }
  return new Date(`${date}T${time}`).getTime();
}

export function dateTimeFilenamePart(date: string, time: string): string {
  if (!time) return date;
  return `${date}T${time.replace(':', '')}`;
}

function parseDateInput(date: string): Date | null {
  if (!date) return null;
  const parsed = new Date(`${date}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatCalendarLabel(date: Date): string {
  return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

function formatRangeButtonLabel(value: DateTimeRangeValue): string {
  const from = value.fromDate || 'Start date';
  const to = value.toDate || 'End date';
  const fromSuffix = value.fromTime ? ` ${value.fromTime}` : '';
  const toSuffix = value.toTime ? ` ${value.toTime}` : '';
  return `${from}${fromSuffix} - ${to}${toSuffix}`;
}

export function DateRangePicker({
  value,
  onApply,
  onClear,
  className,
}: {
  value: DateTimeRangeValue;
  onApply: (value: DateTimeRangeValue) => void;
  onClear: () => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [draftFromDate, setDraftFromDate] = useState(value.fromDate);
  const [draftFromTime, setDraftFromTime] = useState(value.fromTime);
  const [draftToDate, setDraftToDate] = useState(value.toDate);
  const [draftToTime, setDraftToTime] = useState(value.toTime);
  const [calendarMonth, setCalendarMonth] = useState(
    () => parseDateInput(value.fromDate) ?? new Date()
  );

  useEffect(() => {
    if (!open) return;
    setDraftFromDate(value.fromDate);
    setDraftFromTime(value.fromTime || '00:00');
    setDraftToDate(value.toDate);
    setDraftToTime(value.toTime || '23:59');
    setCalendarMonth(parseDateInput(value.fromDate || value.toDate) ?? new Date());
  }, [open, value]);

  const monthCells = useMemo(() => {
    const firstOfMonth = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1);
    const mondayOffset = (firstOfMonth.getDay() + 6) % 7;
    const firstCell = addDays(firstOfMonth, -mondayOffset);
    return Array.from({ length: 42 }, (_, index) => {
      const date = addDays(firstCell, index);
      return {
        date,
        value: toDateInputValue(date),
        inCurrentMonth: date.getMonth() === calendarMonth.getMonth(),
      };
    });
  }, [calendarMonth]);

  const handleSelectDate = (nextValue: string) => {
    if (!draftFromDate || (draftFromDate && draftToDate)) {
      setDraftFromDate(nextValue);
      setDraftToDate('');
      return;
    }
    if (nextValue < draftFromDate) {
      setDraftFromDate(nextValue);
      setDraftToDate('');
      return;
    }
    setDraftToDate(nextValue);
  };

  const applyToday = () => {
    const today = todayDateInputValue();
    setDraftFromDate(today);
    setDraftToDate(today);
    setDraftFromTime('00:00');
    setDraftToTime('23:59');
    setCalendarMonth(parseDateInput(today) ?? new Date());
  };

  const applyLastSevenDays = () => {
    const today = new Date();
    const from = toDateInputValue(addDays(today, -6));
    const to = toDateInputValue(today);
    setDraftFromDate(from);
    setDraftToDate(to);
    setDraftFromTime('00:00');
    setDraftToTime('23:59');
    setCalendarMonth(parseDateInput(to) ?? today);
  };

  const clearDraft = () => {
    setDraftFromDate('');
    setDraftFromTime('');
    setDraftToDate('');
    setDraftToTime('');
    onClear();
  };

  const canApply = !!draftFromDate && !!draftToDate;
  const today = todayDateInputValue();

  return (
    <div className={clsx('relative', className)}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex min-w-[320px] items-center justify-between gap-3 rounded-lg border border-brand-border bg-brand-dark px-3 py-2 text-left text-sm text-brand-text-primary hover:border-brand-text-secondary/50"
      >
        <span className="flex min-w-0 items-center gap-2">
          <CalendarDays className="h-4 w-4 shrink-0 text-brand-orange" />
          <span className="truncate">{formatRangeButtonLabel(value)}</span>
        </span>
        <ChevronRight className={clsx('h-4 w-4 shrink-0 transition', open && 'rotate-90')} />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-[70] mt-2 w-[360px] rounded-lg border border-brand-border bg-brand-surface p-4 shadow-2xl">
          <div className="mb-3 flex items-center justify-between">
            <button
              type="button"
              onClick={() =>
                setCalendarMonth(
                  new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1)
                )
              }
              className="rounded-lg border border-brand-border bg-brand-dark p-2 text-brand-text-secondary hover:text-white"
              aria-label="Previous month"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="text-sm font-semibold text-brand-text-primary">
              {formatCalendarLabel(calendarMonth)}
            </div>
            <button
              type="button"
              onClick={() =>
                setCalendarMonth(
                  new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1)
                )
              }
              className="rounded-lg border border-brand-border bg-brand-dark p-2 text-brand-text-secondary hover:text-white"
              aria-label="Next month"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-semibold uppercase text-brand-text-secondary">
            {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((day) => (
              <div key={day} className="py-1">
                {day}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {monthCells.map((cell) => {
              const isStart = cell.value === draftFromDate;
              const isEnd = cell.value === draftToDate;
              const isInRange =
                !!draftFromDate &&
                !!draftToDate &&
                cell.value > draftFromDate &&
                cell.value < draftToDate;
              const isToday = cell.value === today;
              return (
                <button
                  key={cell.value}
                  type="button"
                  onClick={() => handleSelectDate(cell.value)}
                  className={clsx(
                    'h-9 rounded-md text-sm transition',
                    !cell.inCurrentMonth && 'text-brand-text-secondary/40',
                    cell.inCurrentMonth && 'text-brand-text-primary',
                    isInRange && 'bg-brand-orange/15 text-white',
                    (isStart || isEnd) && 'bg-brand-orange font-bold text-white',
                    isToday && !isStart && !isEnd && 'ring-1 ring-brand-orange/70',
                    !isInRange && !isStart && !isEnd && 'hover:bg-brand-surface-highlight'
                  )}
                >
                  {cell.date.getDate()}
                </button>
              );
            })}
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-brand-text-secondary">
                From time
              </span>
              <input
                type="time"
                value={draftFromTime}
                onChange={(event) => setDraftFromTime(event.target.value)}
                className="w-full rounded-lg border border-brand-border bg-brand-dark px-3 py-2 text-sm text-brand-text-primary"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-brand-text-secondary">
                To time
              </span>
              <input
                type="time"
                value={draftToTime}
                onChange={(event) => setDraftToTime(event.target.value)}
                className="w-full rounded-lg border border-brand-border bg-brand-dark px-3 py-2 text-sm text-brand-text-primary"
              />
            </label>
          </div>

          <div className="mt-4 flex flex-wrap justify-between gap-2">
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={applyToday}
                className="rounded-lg border border-brand-border bg-brand-dark px-3 py-2 text-xs text-brand-text-secondary hover:text-white"
              >
                Today
              </button>
              <button
                type="button"
                onClick={applyLastSevenDays}
                className="rounded-lg border border-brand-border bg-brand-dark px-3 py-2 text-xs text-brand-text-secondary hover:text-white"
              >
                Last 7 days
              </button>
              <button
                type="button"
                onClick={clearDraft}
                className="rounded-lg border border-brand-border bg-transparent px-3 py-2 text-xs text-brand-text-secondary hover:text-white"
              >
                Clear
              </button>
            </div>
            <button
              type="button"
              onClick={() => {
                if (!canApply) return;
                onApply({
                  fromDate: draftFromDate,
                  fromTime: draftFromTime,
                  toDate: draftToDate,
                  toTime: draftToTime,
                });
                setOpen(false);
              }}
              disabled={!canApply}
              className="rounded-lg bg-brand-orange px-4 py-2 text-xs font-semibold text-white disabled:opacity-50"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
